import { PDFDocument, degrees } from 'pdf-lib';
import type { OrderFile, PagePlan, Slot } from './types';
import { allocateLayout } from './layout';
import { contentFingerprint } from './hash';

export const MAX_FILE_BYTES = 50 * 1024 * 1024;

export class PdfInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PdfInputError';
  }
}

let generatedEntryId = 0;

function createParsedEntryId(): string {
  generatedEntryId += 1;
  return `order-${Date.now()}-${generatedEntryId}`;
}

export async function readOrderFile(file: File, entryId = createParsedEntryId()): Promise<OrderFile> {
  if (file.size === 0) throw new PdfInputError('The file is empty. Choose a non-empty single-order PDF.');
  if (file.size > MAX_FILE_BYTES) throw new PdfInputError('The file is larger than 50 MB and was not loaded.');
  if (file.type && file.type !== 'application/pdf') throw new PdfInputError('The selected file is not a PDF.');

  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await file.arrayBuffer());
  } catch {
    throw new PdfInputError('The file could not be read. Try selecting it again.');
  }

  let document: PDFDocument;
  try {
    document = await PDFDocument.load(bytes, {
      ignoreEncryption: false,
      throwOnInvalidObject: true,
      updateMetadata: false,
    });
  } catch {
    throw new PdfInputError('This PDF is unreadable, malformed, encrypted, or password-protected.');
  }
  if (document.getPageCount() !== 1) {
    throw new PdfInputError(`This PDF has ${document.getPageCount()} pages. Each file must contain exactly one order page.`);
  }
  const page = document.getPage(0);
  const { width, height } = page.getSize();
  if (!(width > 0 && height > 0)) throw new PdfInputError('The PDF page has no usable dimensions.');

  const contents = page.node.Contents();
  if (!contents) throw new PdfInputError('The PDF page is empty. Choose a PDF containing a shipping label.');

  const fingerprint = await contentFingerprint(bytes);
  return {
    id: entryId,
    name: file.name,
    size: file.size,
    bytes,
    fingerprint,
    sourceWidth: width,
    sourceHeight: height,
  };
}

function fit(sourceWidth: number, sourceHeight: number, slot: Slot): { width: number; height: number; x: number; y: number } {
  const scale = Math.min(slot.width / sourceWidth, slot.height / sourceHeight);
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;
  const left = slot.left + (slot.width - width) / 2;
  const top = slot.top + (slot.height - height) / 2;
  return { width, height, x: left, y: top };
}

function drawPlacement(outputPage: ReturnType<PDFDocument['addPage']>, embedded: Awaited<ReturnType<PDFDocument['embedPdf']>>[number], plan: PagePlan, order: OrderFile, slot: Slot): void {
  if (slot.rotation === 90) {
    const scale = Math.min(slot.width / order.sourceHeight, slot.height / order.sourceWidth);
    const unrotatedWidth = order.sourceWidth * scale;
    const unrotatedHeight = order.sourceHeight * scale;
    const rotatedWidth = unrotatedHeight;
    const rotatedHeight = unrotatedWidth;
    const left = slot.left + (slot.width - rotatedWidth) / 2;
    const top = slot.top + (slot.height - rotatedHeight) / 2;
    const bottom = plan.size.height - top - rotatedHeight;
    outputPage.drawPage(embedded, {
      x: left + rotatedWidth,
      y: bottom,
      width: unrotatedWidth,
      height: unrotatedHeight,
      rotate: degrees(90),
    });
    return;
  }

  const fitted = fit(order.sourceWidth, order.sourceHeight, slot);
  outputPage.drawPage(embedded, {
    x: fitted.x,
    y: plan.size.height - fitted.y - fitted.height,
    width: fitted.width,
    height: fitted.height,
  });
}

export async function buildCombinedPdf(orders: OrderFile[]): Promise<Uint8Array> {
  if (orders.length === 0) throw new PdfInputError('Add at least one valid order PDF before generating.');
  const output = await PDFDocument.create();
  output.setTitle('Daraz order labels');
  output.setProducer('Daraz Order PDF Automator');
  const plans = allocateLayout(orders.length);

  for (const plan of plans) {
    const page = output.addPage([plan.size.width, plan.size.height]);
    page.setRotation(degrees(0));
    for (const placement of plan.placements) {
      const order = orders[placement.orderIndex];
      const [embedded] = await output.embedPdf(order.bytes, [0]);
      drawPlacement(page, embedded, plan, order, placement.slot);
    }
  }
  return output.save({ useObjectStreams: false });
}
